import React from 'react';
import type { TheoryArticleSections } from '../components/TheoryArticleBody.tsx';

const SQL_KEYWORD = (text: string) => (
  <span className="theory-code-kw">{text}</span>
);

export const THEORY_ARTICLE_BY_SUBLESSON_SLUG: Record<string, TheoryArticleSections> = {
  'what-is-sql': {
    subtitle: 'Основы языка запросов к реляционным базам данных.',
    definition: {
      title: 'Определение',
      paragraphs: [
        'SQL — язык запросов к реляционным базам данных. Он позволяет получать, фильтровать, сортировать и анализировать данные, хранящиеся в таблицах.',
      ],
    },
    keyConstructions: {
      title: 'Термины',
      rows: [
        { term: 'База данных', description: 'набор таблиц' },
        { term: 'Таблица', description: 'структура данных (строки и столбцы)' },
        { term: 'Строка', description: 'одна запись' },
        { term: 'Столбец', description: 'характеристика' },
        { term: 'Запрос', description: 'команда SQL' },
        { term: 'Результат', description: 'возвращённые данные' },
      ],
    },
    operators: {
      title: 'Логика SQL',
      rows: [
        { term: 'SELECT', description: 'что вывести' },
        { term: 'FROM', description: 'откуда взять' },
        { term: 'WHERE', description: 'фильтрация' },
        { term: 'ORDER BY', description: 'сортировка' },
      ],
    },
    miniExample: {
      title: 'Пример',
      code: (
        <>
          {SQL_KEYWORD('SELECT')} name {SQL_KEYWORD('FROM')} employees;
        </>
      ),
      resultTable: {
        headers: ['name'],
        rows: [['Alex'], ['Maria']],
      },
    },
    commonMistakes: {
      title: 'Частые ошибки',
      items: [
        'путать таблицу и базу',
        'писать SQL без структуры',
        'использовать SELECT * без понимания',
      ],
    },
    remember: {
      title: 'Итог',
      items: [
        'SQL работает с таблицами',
        'запрос = логическая цепочка',
        'результат — новая таблица',
      ],
    },
  },
  'databases-tables-rows-columns': {
    subtitle: 'Как устроены базы данных и из чего состоят таблицы.',
    definition: {
      title: 'Определение',
      paragraphs: [
        'База данных — это система хранения структурированных данных, организованных в таблицы.',
      ],
    },
    keyConstructions: {
      title: 'Термины',
      rows: [
        { term: 'База данных', description: 'система хранения' },
        { term: 'Таблица', description: 'набор данных' },
        { term: 'Строка', description: 'запись' },
        { term: 'Столбец', description: 'поле' },
        { term: 'Схема', description: 'структура базы' },
        { term: 'Значение', description: 'конкретные данные' },
      ],
    },
    operators: {
      title: 'Структура данных',
      rows: [
        { term: 'Таблицы', description: 'связаны между собой' },
        { term: 'Строки', description: 'объекты' },
        { term: 'Столбцы', description: 'характеристики' },
      ],
    },
    miniExample: {
      title: 'Пример',
      caption: 'Таблица users:',
      resultTable: {
        headers: ['id', 'name'],
        rows: [['1', 'Alex']],
      },
    },
    commonMistakes: {
      title: 'Частые ошибки',
      items: [
        'путать строку и столбец',
        'думать что таблица = файл',
        'игнорировать структуру данных',
      ],
    },
    remember: {
      title: 'Итог',
      items: [
        'данные хранятся в таблицах',
        'строки = сущности',
        'столбцы = признаки',
      ],
    },
  },
  'data-types': {
    subtitle: 'Какие типы значений можно хранить в столбцах таблицы.',
    definition: {
      title: 'Определение',
      paragraphs: [
        'Типы данных определяют, какие значения могут храниться в столбце.',
      ],
    },
    keyConstructions: {
      title: 'Термины',
      rows: [
        { term: 'INT', description: 'числа' },
        { term: 'VARCHAR', description: 'строки' },
        { term: 'DATE', description: 'даты' },
        { term: 'BOOLEAN', description: 'true/false' },
        { term: 'FLOAT', description: 'дробные числа' },
        { term: 'TEXT', description: 'длинный текст' },
      ],
    },
    operators: {
      title: 'Правила',
      items: [
        'тип выбирается при создании таблицы',
        'нельзя смешивать типы',
        'влияет на операции',
      ],
    },
    miniExample: {
      title: 'Пример',
      code: (
        <>
          age {SQL_KEYWORD('INT')},{'\n'}
          name {SQL_KEYWORD('VARCHAR')}(100)
        </>
      ),
    },
    commonMistakes: {
      title: 'Частые ошибки',
      items: [
        'хранить числа как текст',
        'неверный тип даты',
        'отсутствие ограничений',
      ],
    },
    remember: {
      title: 'Итог',
      items: [
        'типы защищают данные',
        'влияют на операции',
        'задаются при создании',
      ],
    },
  },
};

export function getTheoryArticleSections(
  sublessonSlug: string | undefined
): TheoryArticleSections | null {
  if (!sublessonSlug) return null;
  return THEORY_ARTICLE_BY_SUBLESSON_SLUG[sublessonSlug] ?? null;
}
